import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Package, Users, Wrench, Receipt, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Repair statuses considered "active" — must stay aligned with the backend
// ACTIVE tuple in routes/search.py.
const ACTIVE = new Set(['available', 'waiting_part', 'waiting_client', 'outsourcing', 'liberacion', 'done']);
const STATUS_ES = {
  available: 'Disponible', waiting_part: 'Esperando pieza', waiting_client: 'Esperando cliente',
  outsourcing: 'Servicio externo', liberacion: 'Liberacion', done: 'Terminado',
  delivered: 'Entregado', archived: 'Archivado',
};

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const q = (params.get('q') || '').trim();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPast, setShowPast] = useState(false);
  // Per-client "Ver registros pasados" toggle state, keyed by client-group index
  const [openClientPast, setOpenClientPast] = useState({});

  useEffect(() => {
    if (q.length < 2) return;
    setLoading(true); setError(''); setShowPast(false); setOpenClientPast({});
    axios.get(`${API}/api/search?q=${encodeURIComponent(q)}`, { headers: getAuthHeaders() })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Error de búsqueda'))
      .finally(() => setLoading(false));
  }, [q]);

  if (q.length < 2) {
    return <p className="text-sm text-slate-500">Escribe al menos 2 caracteres y presiona Enter.</p>;
  }

  const invGroups = Object.entries(data?.inventory || {});
  const clients = data?.clients || [];
  const rep = data?.repairs || { bolsa_active: [], bolsa_past: [], by_client: [], other: [] };
  const byClient = rep.by_client || [];
  const sales = data?.sales || [];

  const hasBolsaActive = rep.bolsa_active.length > 0;
  const hasBolsaPast = rep.bolsa_past.length > 0;
  const showPastButton = hasBolsaPast;
  const hasAnyRepair = hasBolsaActive || hasBolsaPast || byClient.length > 0 || rep.other.length > 0;

  const noResults = !loading && !error && data
    && invGroups.length === 0 && clients.length === 0
    && !hasAnyRepair && sales.length === 0;

  return (
    <div data-testid="search-results-page" className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="search-back-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <ArrowLeft className="w-4 h-4" /> Regresar
        </button>
        <h1 className="text-xl font-bold text-[#1D3C6E] font-['Outfit']">Resultados: “{q}”</h1>
      </div>

      {loading && <p className="text-sm text-slate-500">Buscando...</p>}
      {error && <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-700"><AlertCircle className="w-4 h-4 mt-0.5" /><span>{error}</span></div>}
      {noResults && <p className="text-sm text-slate-500" data-testid="search-no-results">Sin resultados.</p>}

      {/* Reparaciones */}
      {hasAnyRepair && (
        <section className="bg-white rounded-lg border border-slate-200 p-4" data-testid="group-repairs">
          <h2 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><Wrench className="w-4 h-4 text-[#1D3C6E]" /> Reparaciones</h2>

          {/* Búsqueda por bolsa: activos resaltados + botón de pasadas */}
          {(hasBolsaActive || hasBolsaPast) && (
            <ul className="divide-y divide-slate-100 mb-2">
              {hasBolsaActive && rep.bolsa_active.map(r => (
                <li key={`ba-${r.id}`} className="py-2 flex items-center gap-3 bg-amber-50 border-l-4 border-[#AFBD20] px-2 -mx-2" data-testid={`repair-active-${r.id}`}>
                  <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 uppercase">Activa</span>
                  <Link to={`/repairs/${r.id}`} className="text-sm text-slate-800 flex-1 truncate">
                    #{r.bag ?? '—'} · {r.client_name} · {r.device_brand} {r.device_model} · <span className="text-slate-500">{r.ticket}</span> · <em>{STATUS_ES[r.status] || r.status}</em>
                  </Link>
                </li>
              ))}
              {!hasBolsaActive && hasBolsaPast && !showPast && (
                <li className="py-2">
                  <button onClick={() => setShowPast(true)} data-testid="view-past-only"
                    className="text-sm text-[#1D3C6E] underline underline-offset-2 hover:text-[#152B50]">
                    Ver registros pasados
                  </button>
                </li>
              )}
              {hasBolsaActive && showPastButton && !showPast && (
                <li className="py-2">
                  <button onClick={() => setShowPast(true)} data-testid="view-past-with-active"
                    className="text-xs text-[#1D3C6E] underline underline-offset-2 hover:text-[#152B50]">
                    Ver registros pasados ({rep.bolsa_past.length})
                  </button>
                </li>
              )}
              {showPast && rep.bolsa_past.map(r => (
                <li key={`bp-${r.id}`} className="py-2 flex items-center gap-3" data-testid={`repair-past-${r.id}`}>
                  <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 uppercase">Pasada</span>
                  <Link to={`/repairs/${r.id}`} className="text-sm text-slate-700 flex-1 truncate">
                    #{r.bag ?? '—'} · {r.client_name} · {r.device_brand} {r.device_model} · <span className="text-slate-500">{r.ticket}</span> · <em>{STATUS_ES[r.status] || r.status}</em>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          {/* Búsqueda por cliente: un grupo por cliente */}
          {byClient.map((grp, gi) => {
            const openPast = !!openClientPast[gi];
            const hasActive = grp.active.length > 0;
            const hasPast = grp.past.length > 0;
            const gid = grp.client_id || `g-${gi}`;
            return (
              <div key={gid} className="mb-3 last:mb-0 rounded-md border border-slate-100 bg-slate-50 p-2" data-testid={`repair-client-group-${gid}`}>
                <p className="text-xs font-semibold text-[#1D3C6E] mb-1.5">
                  {grp.client_name || 'Sin nombre'}
                  {grp.client_phone && <span className="text-slate-500 font-normal"> · {grp.client_phone}</span>}
                </p>
                <ul className="divide-y divide-slate-100">
                  {hasActive && grp.active.map(r => (
                    <li key={`cga-${r.id}`} className="py-1.5 flex items-center gap-3 bg-amber-50 border-l-4 border-[#AFBD20] px-2 -mx-2" data-testid={`repair-active-${r.id}`}>
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 uppercase">Activa</span>
                      <Link to={`/repairs/${r.id}`} className="text-sm text-slate-800 flex-1 truncate">
                        #{r.bag ?? '—'} · {r.device_brand} {r.device_model} · <span className="text-slate-500">{r.ticket}</span> · <em>{STATUS_ES[r.status] || r.status}</em>
                      </Link>
                    </li>
                  ))}
                  {!hasActive && hasPast && !openPast && (
                    <li className="py-1.5">
                      <button onClick={() => setOpenClientPast(s => ({ ...s, [gi]: true }))}
                        data-testid={`view-past-only-${gid}`}
                        className="text-sm text-[#1D3C6E] underline underline-offset-2 hover:text-[#152B50]">
                        Ver registros pasados
                      </button>
                    </li>
                  )}
                  {hasActive && hasPast && !openPast && (
                    <li className="py-1.5">
                      <button onClick={() => setOpenClientPast(s => ({ ...s, [gi]: true }))}
                        data-testid={`view-past-with-active-${gid}`}
                        className="text-xs text-[#1D3C6E] underline underline-offset-2 hover:text-[#152B50]">
                        Ver registros pasados ({grp.past.length})
                      </button>
                    </li>
                  )}
                  {openPast && grp.past.map(r => (
                    <li key={`cgp-${r.id}`} className="py-1.5 flex items-center gap-3" data-testid={`repair-past-${r.id}`}>
                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 uppercase">Pasada</span>
                      <Link to={`/repairs/${r.id}`} className="text-sm text-slate-700 flex-1 truncate">
                        #{r.bag ?? '—'} · {r.device_brand} {r.device_model} · <span className="text-slate-500">{r.ticket}</span> · <em>{STATUS_ES[r.status] || r.status}</em>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}

          {/* Coincidencias por ticket (sin agrupar) */}
          {rep.other.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {rep.other.map(r => (
                <li key={`ro-${r.id}`} className="py-2 flex items-center gap-3" data-testid={`repair-other-${r.id}`}>
                  <Link to={`/repairs/${r.id}`} className="text-sm text-slate-800 flex-1 truncate">
                    #{r.bag ?? '—'} · {r.client_name} · {r.device_brand} {r.device_model} · <span className="text-slate-500">{r.ticket}</span> · <em>{STATUS_ES[r.status] || r.status}</em>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Clientes */}
      {clients.length > 0 && (
        <section className="bg-white rounded-lg border border-slate-200 p-4" data-testid="group-clients">
          <h2 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><Users className="w-4 h-4 text-[#1D3C6E]" /> Clientes</h2>
          <ul className="divide-y divide-slate-100">
            {clients.map(c => (
              <li key={c.id} className="py-2" data-testid={`client-${c.id}`}>
                <Link to={`/clients?open=${c.id}`} className="text-sm text-slate-800">
                  {c.name} · <span className="text-slate-500">{c.phone || '—'}</span>
                  {c.phone2 && <span className="text-slate-500"> / {c.phone2}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Ventas */}
      {sales.length > 0 && (
        <section className="bg-white rounded-lg border border-slate-200 p-4" data-testid="group-sales">
          <h2 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><Receipt className="w-4 h-4 text-[#1D3C6E]" /> Ventas</h2>
          <ul className="divide-y divide-slate-100">
            {sales.map(s => (
              <li key={s.id} className="py-2" data-testid={`sale-${s.id}`}>
                <Link to={`/sales/${s.id}`} className="text-sm text-slate-800">
                  {s.ticket} · {s.client_name || 'Sin cliente'} · ${Number(s.total || 0).toFixed(2)}
                  {s.is_refund && <span className="ml-1 text-[10px] text-rose-600 font-semibold">REEMBOLSO</span>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Inventario agrupado por categoria */}
      {invGroups.map(([cat, items]) => (
        <section key={cat} className="bg-white rounded-lg border border-slate-200 p-4" data-testid={`group-inventory-${cat}`}>
          <h2 className="text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2"><Package className="w-4 h-4 text-[#1D3C6E]" /> {cat}</h2>
          <ul className="divide-y divide-slate-100">
            {items.map(it => (
              <li key={it.id} className="py-2" data-testid={`inv-${it.id}`}>
                <Link to={`/inventory?open=${it.id}`} className="text-sm text-slate-800">
                  {it.name}
                  {it.sku && <span className="text-slate-500"> · {it.sku}</span>}
                  {it.description && <span className="text-slate-500"> · {it.description}</span>}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
